import React, { Component } from "react";
import axios from "axios";
import {
  PrismButton,
  PrismCheckbox,
  PrismInputText,
  PrismInputTextarea,
} from "@prism-ui/react";
import { toast } from "../Toaster/toast";
import PageHeader from "../PageHeader";
import Loader from "../Loader";
import "./Project.css";
import { FileInput } from '../Files/';
import clearLogoSrc from "../../assets/images/clear-logo.svg";

export default class ProjectForm extends Component {
  state = {
    loading: true,
    error: null,
    possibleTags: [],
    project: {
      title: "",
      description: "",
      tags: [],
      logo: ""
    },
    saved: false,
    invalidImageTypeSelected: false
  };

  componentDidMount() {
    this.loadProject(this.props.id);
    this.loadTags();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  loadTags() {
    return axios
      .get(`/api/tags`)
      .then((response) => {
        const tags = [...response.data].sort((a, b) => a.name.localeCompare(b.name));
        this.setState({
          possibleTags: tags.filter(tag => !tag.isHidden),
        });
      })
      .catch((error) => {
        this.setState({
          error: "Unable to load possible tags!",
        });
      });
  }

  loadProject(id) {
    return axios
      .get(`/api/projects/${id}`)
      .then((response) => {
        const { logo, year } = response.data;

        if (logo) {
          if (logo.indexOf('api/files') < 0 && logo !== 'GENERIC') {
            response.data.logo = `api/files/images/project-fair/${year}/logos/${logo}`;
          }

          if (logo === 'GENERIC') {
            // set default logo to the clearLogo
            response.data.logo = clearLogoSrc;
          }
        }

        this.setState({
          loading: false,
          project: response.data,
        });
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error: "Unable to load form!",
        });
      });
  }

  handleSubmit(e) {
    e.preventDefault();

    // Validate Form
    let form = e.currentTarget;
    let isValid = form.checkValidity() && !this.state.invalidImageTypeSelected;
    if (!isValid) {
      let { error } = this.state;
      error = "Invalid fields submitted!"
      this.setState({ error });
      return false;
    }

    // If valid, send to the backend
    axios
      .put(`/api/projects/${this.state.project.id}`, this.state.project)
      .then((res) => {
        if (res) {
          this.setState({
            saved: true,
            error: null
          });
          toast.success('Project saved successfully!', { autoClose: 2000 });
        }
      })
      .catch((err) => {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to save project';
        this.setState({ error: errorMessage });
        toast.error(errorMessage, { autoClose: 3000 });
      });
  }

  // Prism inputs report their value on `event.detail` rather than on a native
  // target, so the field name is captured here instead of read off the event.
  handlePrismChange = (name) => (event) => {
    this.setProjectField(name, event.detail?.value ?? null);
  };

  handleActiveChange = (event) => {
    this.setProjectField('active', !!event.detail?.checked);
  };

  setProjectField = (name, value) => {
    let { project, error } = this.state;

    project[name] = value;

    // reset the error state when a user updates the form
    if (error === "Invalid fields submitted!") {
      error = null;
    }

    this.setState({ project, error, saved: false });
  };

  /*
   * The tag id is passed explicitly rather than read from the DOM. The old
   * handler stored `e.target.value`, which is a string, while the checked test
   * compares it against the numeric `tag.id` with `===`. That mismatch was
   * invisible while the checkboxes were uncontrolled, but a controlled Prism
   * checkbox would immediately render itself back to unchecked.
   */
  handleTagToggle = (tagId) => (event) => {
    const project = this.state.project;

    if (event.detail?.checked) {
      if (!project.tags.some((pt) => pt.tag_id === tagId)) {
        project.tags.push({ tag_id: tagId });
      }
    } else {
      project.tags = project.tags.filter((pt) => pt.tag_id !== tagId);
    }

    this.setState({ project, saved: false });
  };

  handleCancel(e) {
    if (this.props.onCancel) {
      this.props.onCancel(e.target.value);
    }
  }

  invalidFileSelected = (invalidFileSelected) => {
    this.setState({ invalidImageTypeSelected: invalidFileSelected });
  }

  render = () => {
    if (this.state.loading) {
      return <Loader />;
    }

    let project = this.state.project;
    return (
      <>
        <PageHeader title={(project.id ? "Edit" : "Create") + " Project"} />
        <div className="form-container">
          <form
            key={project.id}
            id="event-form"
            onSubmit={this.handleSubmit}
          >
            <h2>Project Information</h2>
            <FileInput object={project} fileKey="logo" entityType="Logo" allowedTypes={['jpeg', 'jpg', 'png']} invalidFileTypeCallback={this.invalidFileSelected} />
            <div className="form-group">
              <PrismInputText
                name="title"
                label="Title"
                value={project.title || ""}
                required
                onPrismChange={this.handlePrismChange('title')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="description"
                label="Description"
                value={project.description || ""}
                onPrismChange={this.handlePrismChange('description')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="video_source"
                label="Live Video Source"
                value={project.video_source || ""}
                placeholder="Microsoft Teams"
                onPrismChange={this.handlePrismChange('video_source')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="video_url"
                label="Live Channel Link (Meeting Invite)"
                value={project.video_url || ""}
                onPrismChange={this.handlePrismChange('video_url')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="video_stream_url"
                label="Video Stream Link"
                value={project.video_stream_url || ""}
                onPrismChange={this.handlePrismChange('video_stream_url')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="spark_url"
                label="Spark Link"
                value={project.spark_url || ""}
                onPrismChange={this.handlePrismChange('spark_url')}
              />
            </div>

            <div className="form-group">
              <PrismInputTextarea
                name="thanks"
                label="Special Thanks"
                value={project.thanks || ""}
                onPrismChange={this.handlePrismChange('thanks')}
              />
            </div>

            <div className="form-group">
              <PrismCheckbox
                id="active"
                name="active"
                label="Active"
                checked={!!project.active}
                onPrismChange={this.handleActiveChange}
              />
            </div>

            <div className="form-group">
              <label className="form-group-label">Tags</label>
              <div className="project-tags grid-container">
                {this.state.possibleTags.map((t) => {
                  return (
                    <PrismCheckbox
                      id={`${t.id}-${t.name}`}
                      key={`${t.id}-${t.name}`}
                      name={`tag-${t.id}`}
                      value={String(t.id)}
                      checked={this.state.project.tags.some(
                        (x) => x.tag_id === t.id
                      )}
                      label={t.name}
                      className="grid-item"
                      onPrismChange={this.handleTagToggle(t.id)}
                    />
                  );
                })}
              </div>
            </div>
          </form>
          <hr />
          {/* Outside the <form>; prism-button resolves `form` by id and calls requestSubmit(). */}
          <PrismButton
            type="submit"
            form="event-form"
            disabled={this.state.saved}
          >
            Save Project
          </PrismButton>{" "}
          <PrismButton
            tone="secondary"
            display="outline"
            onPrismClick={this.handleCancel}
          >
            Back
          </PrismButton>
          {this.state.error ? <h3>Error: {this.state.error}</h3> : ""}
        </div>
      </>
    );
  };
}
